import qs from 'qs'

import { TransactionOriginatorType } from '@features/transactions/enums/transactionOriginatorType'
import { TransactionOriginatorTypeGroup } from '@features/transactions/enums/transactionOriginatorTypeGroup'
import { TransactionSortProperty } from '@features/transactions/enums/transactionSortProperty'
import { Transaction } from '@features/transactions/types/transaction'

import { SortDirection } from '../../enums/sortDirection'
import { Metable } from '../../types/metable'
import { getRequest } from '../../utils'
import { getOriginatorTypesFromGroup } from './routes/TransactionsList/utils/getOriginatorTypesFromGroup'

export interface FetchTransactionsOptions {
  /* Examples:
   * - entryDatePeriod=all
   * - entryDatePeriod=dates:2019-04-23...2019-06-14
   * - entryDatePeriod=from:2019-05-08
   * - entryDatePeriod=half:2019-2
   * - entryDatePeriod=month:2019-06
   * - entryDatePeriod=quarter:2019-2
   * - entryDatePeriod=through:2019-05-03
   * - entryDatePeriod=year:2019
   * - entryDatePeriod=fiscalyear:2019
   */
  entryDatePeriod?: string
  withVoided?: boolean
  journalCode?: string[]
  organizationId?: string
  originatorReference?: string
  originatorType?: TransactionOriginatorType[]
  // Possibility to filter by originator type groups, overrides originatorType filter
  originatorTypeGroup?: TransactionOriginatorTypeGroup[]
  q?: string
  page?: number
  pageSize?: number
  sortDirection?: SortDirection
  sortProperty?: TransactionSortProperty
  withHasVoucher?: boolean
  withCreator?: boolean
  withPostings?: boolean
}

export interface FetchTransactionsResponseData extends Metable {
  transactions: Transaction[]
}

export const fetchTransactions = ({
  entryDatePeriod,
  withVoided,
  journalCode,
  organizationId,
  originatorReference,
  originatorType,
  originatorTypeGroup,
  page,
  pageSize,
  q,
  sortDirection,
  sortProperty,
  withHasVoucher,
  withCreator,
  withPostings,
}: FetchTransactionsOptions): Promise<FetchTransactionsResponseData> => {
  const queryParams = qs.stringify({
    organizationId,
    // Property 'withVoided' use cases:
    // if true will return only transactions with isVoid or isVoided true
    // if false will return transactions with isVoid or isVoided false
    // if undefined - no filtering on this prop
    ...(withVoided ? {} : { isVoided: false }),
    ...(originatorReference ? { originatorReference } : {}),
    ...(originatorType?.length ? { originatorType } : {}),
    ...(originatorTypeGroup?.length ? { originatorType: getOriginatorTypesFromGroup(originatorTypeGroup) } : {}),
    ...(journalCode?.length ? { journalCode } : {}),
    ...(page ? { page } : {}),
    ...(pageSize ? { pageSize } : {}),
    ...(q ? { q } : {}),
    ...(sortDirection ? { sortDirection } : {}),
    ...(sortProperty ? { sortProperty } : {}),
    ...(entryDatePeriod ? { entryDatePeriod } : {}),
    ...(withHasVoucher ? { withHasVoucher: 1 } : {}),
    ...(withCreator ? { withCreator } : {}),
    ...(withPostings ? { include: 'transaction.postings:embed' } : {}),
  })

  return getRequest(`/v2/transactions?${queryParams}`)
}

export interface FetchTransactionData {
  transaction: Transaction
}

export const fetchTransaction = (transactionId: string): Promise<FetchTransactionData> => {
  const queryParams = qs.stringify({
    include: 'transaction.postings',
    withCreator: true,
  })

  return getRequest(`/v2/transactions/${transactionId}?${queryParams}`)
}
